import {
    Button,
    Callout,
    DialogBody,
    HTMLSelect,
    HTMLTable,
    InputGroup,
    MenuItem,
    NumericInput,
    Tag
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ItemRenderer, Select } from "@blueprintjs/select";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import {
    useGetPoiBusesByNameOrIdLazyQuery,
    useGetSpeculativeProjectsForCopyQuery
} from "generated/graphql";
import React, { useEffect, useMemo, useState } from "react";
import { QueueProject } from "types/queueProjectsType";
import { Scope } from "types/scope";
import { ScopeViewType } from "types/scopeViewType";
import {
    acceptsChargingMw,
    buildPoiEligibilityFilters,
    CHARGING_RATIO,
    convertToPriorSpeculativeStudies,
    formatSpeculativePoiBus,
    formatStudyBusUtilities,
    getChargingMwError,
    getSpeculativeFuelTypeLabel,
    isNameplateMwValid,
    makeEmptySpeculativeProjectDraft,
    MAX_NAMEPLATE_MW,
    MAX_SPECULATIVE_PROJECTS,
    MIN_CHARGING_MW,
    MIN_NAMEPLATE_MW,
    readNumericInputMw,
    SpeculativePoiBus,
    SpeculativeProjectDraft,
    SPECULATIVE_FUEL_TYPE_OPTIONS,
    toSpeculativePoiBuses
} from "utils/speculativeProjectUtils";
import CopyFromPriorStudyDialog from "./CopyFromPriorStudyDialog";
import "./NewProjectsPanel.scss";

/**
 * Step of the Re-study Bus wizard for speculative ("new") projects: queue
 * projects that aren't in the base queue file — the user's own other projects,
 * expected competitor submissions, or new queue entries not yet published.
 * Added projects are always modeled as included, alongside the base-file
 * projects kept in the previous step.
 */

const MIN_BUS_QUERY_LENGTH = 2;
const BUS_QUERY_DEBOUNCE_MS = 300;

/**
 * Server-backed bus typeahead. Candidates are restricted to the buses this
 * study can model: in the base scope's models, so the pipeline can find the
 * picked `nira_bus_id`, and in the study bus's utility, so the study dispatches
 * the project rather than screening it out.
 */
const usePoiBusSearch = (
    query: string,
    baseScope: Scope,
    scopeViews: ReadonlyArray<ScopeViewType>
): {
    readonly buses: ReadonlyArray<SpeculativePoiBus>;
    readonly loading: boolean;
    readonly failed: boolean;
} => {
    // Read as fields rather than as the bus: `useBusPageConfiguration` builds a
    // new bus object on every render, so depending on it below would restart
    // the search debounce while the user types.
    const { region, utilityDischarging, utilityStorage } =
        useBusPageConfiguration().bus;
    const [buses, setBuses] = useState<ReadonlyArray<SpeculativePoiBus>>([]);
    // True from the keystroke until that keystroke's request settles. Apollo's
    // `loading` only covers the request itself, so the debounce window would
    // otherwise read as a finished search with no rows and flash the
    // "no matching buses" message before the results land.
    const [searchPending, setSearchPending] = useState(false);
    const [getPoiBuses, { loading, error }] =
        useGetPoiBusesByNameOrIdLazyQuery();

    useEffect(() => {
        const trimmed = query.trim();
        // With no selected views the per-view filter below would be an empty
        // `_and`, which Hasura reads as "no restriction" — that would drop the
        // base-scope guard entirely, so return nothing instead.
        if (trimmed.length < MIN_BUS_QUERY_LENGTH || scopeViews.length === 0) {
            setBuses([]);
            setSearchPending(false);
            return;
        }
        // The Select offers every row in `buses`, so the previous query's
        // candidates stay pickable until this query's request lands. Drop them
        // now, before the debounce, or a fast Enter saves a bus the user is no
        // longer searching for.
        setBuses([]);
        setSearchPending(true);
        let cancelled = false;
        const timeout = setTimeout(async () => {
            const result = await getPoiBuses({
                variables: {
                    region,
                    query: `%${trimmed}%`,
                    scope_view_filters: [
                        ...buildPoiEligibilityFilters(
                            baseScope.scope,
                            scopeViews,
                            { utilityDischarging, utilityStorage }
                        )
                    ]
                }
            });
            // A slower earlier request must not overwrite a newer query's
            // candidates after this effect has been cleaned up.
            if (cancelled) {
                return;
            }
            setSearchPending(false);
            // Apollo can resolve a failed request with the previous
            // successful `data`, so a failure would otherwise repopulate the
            // dropdown with the old query's rows — and the "Bus search
            // failed" no-results message only renders while `buses` is empty.
            if (result.error !== undefined) {
                setBuses([]);
                return;
            }
            setBuses(toSpeculativePoiBuses(result.data?.buses ?? []));
        }, BUS_QUERY_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [
        query,
        region,
        utilityDischarging,
        utilityStorage,
        baseScope.scope,
        scopeViews,
        getPoiBuses
    ]);

    return {
        buses,
        loading: loading || searchPending,
        failed: error !== undefined
    };
};

const PoiBusSelect: React.FC<{
    readonly selectedBus: SpeculativePoiBus | undefined;
    readonly showError: boolean;
    readonly baseScope: Scope;
    readonly scopeViews: ReadonlyArray<ScopeViewType>;
    readonly onChange: (poiBus: SpeculativePoiBus) => void;
}> = ({ selectedBus, showError, baseScope, scopeViews, onChange }) => {
    const [query, setQuery] = useState("");
    const { bus } = useBusPageConfiguration();
    const { buses, loading, failed } = usePoiBusSearch(
        query,
        baseScope,
        scopeViews
    );
    // Named in the empty state: with the filter on, "no matching buses" alone
    // reads as a broken search rather than as the rule it is.
    const studyUtilities = formatStudyBusUtilities(bus);

    const renderBus: ItemRenderer<SpeculativePoiBus> = (
        poiBus,
        { handleClick, handleFocus, modifiers }
    ) => (
        <MenuItem
            key={poiBus.niraBusId}
            // The utility goes under the name rather than beside the bus id:
            // appending it to the one-line label ate the name's width and cut
            // the voltage off it, and the voltage is half of what identifies a
            // POI. Naming it at all is the only place a candidate's own utility
            // is visible — `owner_name`, which the bus page shows, merges PACE
            // and PACW into a single "PacifiCorp".
            text={
                poiBus.utilities === undefined ? (
                    poiBus.displayName
                ) : (
                    <>
                        {poiBus.displayName}
                        <div className="NewProjectsPanel-poi-utility bp5-text-small bp5-text-muted">
                            {poiBus.utilities}
                        </div>
                    </>
                )
            }
            label={poiBus.busId}
            roleStructure="listoption"
            active={modifiers.active}
            onClick={handleClick}
            onFocus={handleFocus}
        />
    );

    return (
        <Select<SpeculativePoiBus>
            items={[...buses]}
            // Filtering happens server-side; render whatever the query returned.
            itemListPredicate={(_query, items) => items}
            itemRenderer={renderBus}
            onItemSelect={onChange}
            query={query}
            onQueryChange={setQuery}
            noResults={
                <MenuItem
                    disabled
                    text={
                        <div className="NewProjectsPanel-poi-no-results">
                            {query.trim().length < MIN_BUS_QUERY_LENGTH
                                ? "Type a bus name or number…"
                                : loading
                                ? "Searching…"
                                : failed
                                ? "Bus search failed. Try again."
                                : studyUtilities === undefined
                                ? "No matching buses in the base scope"
                                : `No matching ${studyUtilities} buses in the base scope`}
                        </div>
                    }
                />
            }
            popoverProps={{
                minimal: true,
                matchTargetWidth: true
            }}
            resetOnSelect
        >
            <Button
                fill
                alignText="left"
                className="NewProjectsPanel-poi-button"
                textClassName="NewProjectsPanel-poi-button-text"
                rightIcon={IconNames.DOUBLE_CARET_VERTICAL}
                intent={
                    showError && selectedBus === undefined ? "danger" : "none"
                }
                // Long substation names ellipsise in the button; hover shows it all.
                title={
                    selectedBus === undefined
                        ? undefined
                        : formatSpeculativePoiBus(selectedBus)
                }
                text={
                    selectedBus === undefined
                        ? "Select bus"
                        : formatSpeculativePoiBus(selectedBus)
                }
            />
        </Select>
    );
};

/**
 * Informational only: a project of the same fuel type already at this row's bus
 * is very likely a double-count, whether it sits in the base queue file or on
 * another row of this step. Nothing downstream catches the second case — the
 * pipeline's uniqueness check is on `project_number`, which the step assigns
 * per row — so both rows are modeled and dispatched. Never blocks Next.
 */
const useDuplicateNotes = (
    draft: SpeculativeProjectDraft,
    baseQueueProjects: ReadonlyArray<QueueProject>,
    allDrafts: ReadonlyArray<SpeculativeProjectDraft>
): ReadonlyArray<string> => {
    return useMemo(() => {
        const niraBusId = draft.poiBus?.niraBusId;
        if (niraBusId === undefined) {
            return [];
        }
        const fuelTypeLabel = getSpeculativeFuelTypeLabel(draft.fuelType);
        const notes: Array<string> = [];

        const duplicateRows = allDrafts.filter(
            (other) =>
                other.id !== draft.id &&
                other.poiBus?.niraBusId === niraBusId &&
                other.fuelType === draft.fuelType
        );
        if (duplicateRows.length === 1) {
            notes.push(
                `Another new project at this bus is also ${fuelTypeLabel}. Both are modeled.`
            );
        } else if (duplicateRows.length > 1) {
            notes.push(
                `${duplicateRows.length} other new projects at this bus are also ${fuelTypeLabel}. All are modeled.`
            );
        }

        const matches = baseQueueProjects.filter(
            (project) =>
                project.niraBusId === niraBusId &&
                project.fuelType.toLowerCase() === draft.fuelType
        );
        if (matches.length === 1) {
            notes.push(
                `${matches[0].projectNumber} is already in the queue file at this bus. Review in the previous step.`
            );
        } else if (matches.length > 1) {
            notes.push(
                `${matches.length} queue projects at this bus are also ${fuelTypeLabel}. Review in the previous step.`
            );
        }

        return notes;
    }, [draft.id, draft.poiBus, draft.fuelType, baseQueueProjects, allDrafts]);
};

const NewProjectRowFields: React.FC<{
    readonly draft: SpeculativeProjectDraft;
    // Only true once the user has tried to leave the step with this row
    // incomplete. A freshly added row is pristine and must not render as an error.
    readonly showErrors: boolean;
    readonly baseScope: Scope;
    readonly scopeViews: ReadonlyArray<ScopeViewType>;
    readonly baseQueueProjects: ReadonlyArray<QueueProject>;
    // Every row on the step, this one included; the duplicate check drops it by
    // id. Passed whole so the memo keys on the array the panel already holds.
    readonly allDrafts: ReadonlyArray<SpeculativeProjectDraft>;
    readonly onChange: (draft: SpeculativeProjectDraft) => void;
    readonly onDelete: () => void;
}> = ({
    draft,
    showErrors,
    baseScope,
    scopeViews,
    baseQueueProjects,
    allDrafts,
    onChange,
    onDelete
}) => {
    const duplicateNotes = useDuplicateNotes(
        draft,
        baseQueueProjects,
        allDrafts
    );
    const chargingEnabled = acceptsChargingMw(draft.fuelType);
    const nameInvalid = showErrors && draft.name.trim().length === 0;
    // A copied row whose POI didn't survive the copy is a known problem, not a
    // field the user hasn't reached yet, so it says so before any Next attempt.
    const poiUnavailable =
        draft.poiBus === undefined && draft.unavailablePoiBusId !== undefined;
    const poiInvalid = showErrors && draft.poiBus === undefined;
    const nameplateInvalid =
        showErrors && !isNameplateMwValid(draft.nameplateMw);
    // Storage-like fuels need a charging MW in range; without this the row is
    // incomplete and Next holds, so it has to say why like every other cell.
    const chargingError = showErrors ? getChargingMwError(draft) : undefined;

    const handleFuelTypeChange = (fuelType: string) => {
        const nextChargingEnabled = acceptsChargingMw(fuelType);
        onChange({
            ...draft,
            fuelType,
            chargingMw: nextChargingEnabled
                ? draft.chargingMw ??
                  (draft.nameplateMw === undefined
                      ? undefined
                      : Math.round(draft.nameplateMw * CHARGING_RATIO))
                : undefined
        });
    };

    const handleNameplateChange = (
        nameplateMw: number,
        nameplateAsString: string
    ) => {
        const value = readNumericInputMw(nameplateMw, nameplateAsString);
        // A charging MW still equal to what the previous nameplate derived was
        // never typed by the user, so re-deriving it discards nothing.
        const autoFromPrevious =
            draft.nameplateMw === undefined
                ? undefined
                : Math.round(draft.nameplateMw * CHARGING_RATIO);
        const chargingIsAutoDerived =
            draft.chargingMw === undefined ||
            draft.chargingMw === autoFromPrevious;
        onChange({
            ...draft,
            nameplateMw: value,
            // Charging MW follows the nameplate until the user overrides it.
            chargingMw:
                chargingEnabled && chargingIsAutoDerived
                    ? value === undefined
                        ? undefined
                        : Math.round(value * CHARGING_RATIO)
                    : draft.chargingMw
        });
    };

    return (
        <tr>
            <td>
                <InputGroup
                    fill
                    value={draft.name}
                    placeholder="Project name"
                    intent={nameInvalid ? "danger" : "none"}
                    onChange={(e) =>
                        onChange({ ...draft, name: e.target.value })
                    }
                />
                {duplicateNotes.map((note) => (
                    <div
                        key={note}
                        className="NewProjectsPanel-duplicate-note bp5-text-small"
                    >
                        {note}
                    </div>
                ))}
            </td>
            <td>
                <PoiBusSelect
                    selectedBus={draft.poiBus}
                    showError={showErrors || poiUnavailable}
                    baseScope={baseScope}
                    scopeViews={scopeViews}
                    onChange={(poiBus) =>
                        onChange({
                            ...draft,
                            poiBus,
                            unavailablePoiBusId: undefined,
                            unresolvedPoiNiraBusId: undefined
                        })
                    }
                />
                {poiUnavailable && (
                    <div className="NewProjectsPanel-error bp5-text-small">
                        Bus {draft.unavailablePoiBusId} isn&apos;t available in
                        this study, pick another
                    </div>
                )}
                {poiInvalid && !poiUnavailable && (
                    <div className="NewProjectsPanel-error bp5-text-small">
                        POI is required
                    </div>
                )}
            </td>
            <td>
                <HTMLSelect
                    fill
                    value={draft.fuelType}
                    onChange={(e) => handleFuelTypeChange(e.target.value)}
                >
                    {SPECULATIVE_FUEL_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </HTMLSelect>
            </td>
            <td>
                <NumericInput
                    value={draft.nameplateMw ?? ""}
                    onValueChange={handleNameplateChange}
                    intent={nameplateInvalid ? "danger" : "none"}
                    min={MIN_NAMEPLATE_MW}
                    max={MAX_NAMEPLATE_MW}
                    stepSize={10}
                    buttonPosition="none"
                    fill
                />
                {nameplateInvalid && (
                    <div className="NewProjectsPanel-error bp5-text-small">
                        {MIN_NAMEPLATE_MW}–{MAX_NAMEPLATE_MW} MW
                    </div>
                )}
            </td>
            <td>
                {chargingEnabled ? (
                    <>
                        <NumericInput
                            value={draft.chargingMw ?? ""}
                            onValueChange={(value, valueAsString) =>
                                onChange({
                                    ...draft,
                                    chargingMw: readNumericInputMw(
                                        value,
                                        valueAsString
                                    )
                                })
                            }
                            intent={
                                chargingError !== undefined ? "danger" : "none"
                            }
                            min={MIN_CHARGING_MW}
                            max={MAX_NAMEPLATE_MW}
                            stepSize={10}
                            buttonPosition="none"
                            fill
                        />
                        {chargingError !== undefined && (
                            <div className="NewProjectsPanel-error bp5-text-small">
                                {chargingError}
                            </div>
                        )}
                    </>
                ) : (
                    <span className="bp5-text-muted">—</span>
                )}
            </td>
            <td>
                <Button
                    minimal
                    small
                    icon={IconNames.TRASH}
                    onClick={onDelete}
                    aria-label="Remove project"
                />
            </td>
        </tr>
    );
};

const NewProjectsPanel: React.FC<{
    readonly drafts: ReadonlyArray<SpeculativeProjectDraft>;
    // Ids of the rows the user has already tried to submit. Errors are revealed
    // per row, so adding a fresh row after a failed Next starts clean again.
    readonly validatedDraftIds: ReadonlySet<string>;
    readonly baseScope: Scope;
    readonly scopeViews: ReadonlyArray<ScopeViewType>;
    readonly baseQueueProjects: ReadonlyArray<QueueProject>;
    readonly onDraftsChange: (
        drafts: ReadonlyArray<SpeculativeProjectDraft>
    ) => void;
}> = ({
    drafts,
    validatedDraftIds,
    baseScope,
    scopeViews,
    baseQueueProjects,
    onDraftsChange
}) => {
    const { bus } = useBusPageConfiguration();
    const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
    const studyUtilities = formatStudyBusUtilities(bus);

    // Every local study in this region the user's team can see that has
    // speculative projects, newest first. Row-level permissions do the
    // filtering, so this is exactly the set they are allowed to copy from.
    const { data: priorStudiesData } = useGetSpeculativeProjectsForCopyQuery({
        variables: { region: bus.region },
        // The client default is cache-first, and creating a study does not
        // refetch this query, so a study made this session would never show
        // up in the copy list without revalidating on mount.
        fetchPolicy: "cache-and-network"
    });
    const priorStudies = useMemo(
        () =>
            convertToPriorSpeculativeStudies(
                priorStudiesData?.queue_projects ?? []
            ),
        [priorStudiesData]
    );

    const addedMw = drafts.reduce(
        (sum, draft) => sum + (draft.nameplateMw ?? 0),
        0
    );
    const atProjectCap = drafts.length >= MAX_SPECULATIVE_PROJECTS;

    return (
        <DialogBody className="NewProjectsPanel">
            <p className="bp5-text-muted">
                Add projects that aren&apos;t in the base queue file — projects
                you&apos;re developing, expected competitor submissions, or new
                queue entries not yet published. They are modeled as included,
                alongside the base projects you kept in the previous step.
                {studyUtilities !== undefined &&
                    ` POIs are limited to buses in ${studyUtilities} — a project only counts against this bus inside the same utility.`}
            </p>
            <div className="NewProjectsPanel-stats">
                <Tag minimal round intent="primary">
                    {drafts.length} new{" "}
                    {drafts.length === 1 ? "project" : "projects"}
                </Tag>
                <Tag minimal round intent="primary">
                    {addedMw.toLocaleString()} MW added
                </Tag>
            </div>
            {drafts.length === 0 ? (
                <Callout intent="none" icon={IconNames.INFO_SIGN}>
                    No new projects. Add one below
                    {priorStudies.length > 0
                        ? ", copy a set from a previous study,"
                        : ","}{" "}
                    or continue with the base-file assumptions from the previous
                    step.
                </Callout>
            ) : (
                <div className="NewProjectsPanel-table-wrapper">
                    <HTMLTable compact className="NewProjectsPanel-table">
                        <colgroup>
                            <col className="NewProjectsPanel-col-name" />
                            <col className="NewProjectsPanel-col-poi" />
                            <col className="NewProjectsPanel-col-fuel" />
                            <col className="NewProjectsPanel-col-mw" />
                            <col className="NewProjectsPanel-col-mw" />
                            <col className="NewProjectsPanel-col-actions" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Project Name</th>
                                <th>POI</th>
                                <th>Fuel Type</th>
                                <th>Nameplate (MW)</th>
                                <th>Charging (MW)</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {drafts.map((draft, index) => (
                                <NewProjectRowFields
                                    key={draft.id}
                                    draft={draft}
                                    showErrors={validatedDraftIds.has(draft.id)}
                                    baseScope={baseScope}
                                    scopeViews={scopeViews}
                                    baseQueueProjects={baseQueueProjects}
                                    allDrafts={drafts}
                                    onChange={(next) =>
                                        onDraftsChange(
                                            drafts.map((existing, i) =>
                                                i === index ? next : existing
                                            )
                                        )
                                    }
                                    onDelete={() =>
                                        onDraftsChange(
                                            drafts.filter((_, i) => i !== index)
                                        )
                                    }
                                />
                            ))}
                        </tbody>
                    </HTMLTable>
                </div>
            )}
            <div className="NewProjectsPanel-actions">
                <Button
                    minimal
                    intent="primary"
                    icon={IconNames.PLUS}
                    text="Add project"
                    disabled={atProjectCap}
                    onClick={() =>
                        onDraftsChange([
                            ...drafts,
                            makeEmptySpeculativeProjectDraft()
                        ])
                    }
                />
                {priorStudies.length > 0 && (
                    <Button
                        minimal
                        intent="primary"
                        icon={IconNames.DUPLICATE}
                        text="Copy from a previous study…"
                        disabled={atProjectCap}
                        onClick={() => setIsCopyDialogOpen(true)}
                    />
                )}
            </div>
            {priorStudies.length > 0 && (
                <CopyFromPriorStudyDialog
                    isOpen={isCopyDialogOpen}
                    studies={priorStudies}
                    region={bus.region}
                    studyBus={bus}
                    baseScope={baseScope}
                    scopeViews={scopeViews}
                    existingDraftCount={drafts.length}
                    onClose={() => setIsCopyDialogOpen(false)}
                    onCopy={(copied) => onDraftsChange([...drafts, ...copied])}
                />
            )}
        </DialogBody>
    );
};

export default NewProjectsPanel;
